import {
	type AnyArityOneFunction,
	type ArityOneParameter,
} from '@post-office/universal-contracts/pipeline/constraints';
import { HIGH_CARDINALITY_METRIC_TAG_OVERRIDES } from '@post-office/observability';

import {
	type AnyBackendMessageStage,
	type AnyBackendPlacementStage,
	type AnyPayload,
	type AnySyncBackendMessageStage,
	type PlacementBackendRuntimeResult,
	type RequestContext,
	type SyncRequestContext,
} from '../../types';

const HISTOGRAM_LATENCY_INTERVAL_BUCKETS: number[] = [25, 50, 100, 150, 200, 250, 500, 1000];

const timerName: string = 'post-office.placement-v2.stage.execute';

const counterName: string = 'post-office.placement-v2.stage.result';

export const UNIFIED_FINISH_LOGS_GATE = 'post-office_enable-unified-finish-logs';

const hasDelta = (
	tags: AnyPayload | undefined,
): tags is AnyPayload & { delta: { number: number } } => {
	if (!tags || typeof tags.delta !== 'object' || tags.delta === null) {
		return false;
	}
	const delta = tags.delta as { number?: number };
	// A delta exists when the message count has changed (messages were filtered or added)
	return typeof delta.number === 'number' && delta.number !== 0;
};

const createTimedStage =
	<T extends (params: { request: SyncRequestContext }) => unknown>({
		tag,
	}: {
		tag?: ({
			input,
			output,
		}: {
			input: ArityOneParameter<T>;
			output?: Awaited<ReturnType<T>>;
		}) => AnyPayload;
	} = {}) =>
	(stage: T): T =>
		(async (params: ArityOneParameter<T>) => {
			const startTime = performance.now();

			const { stop } = params.request.metrics
				.histogram(
					timerName,
					HISTOGRAM_LATENCY_INTERVAL_BUCKETS,
					HIGH_CARDINALITY_METRIC_TAG_OVERRIDES,
				)
				.measure();

			const result = stage(params);

			let tags: AnyPayload | undefined;

			try {
				const unwrappedResult = (result instanceof Promise ? await result : result) as Awaited<
					ReturnType<T>
				>;

				try {
					tags = tag?.({ input: params, output: unwrappedResult });
				} catch {
					params.request.logger.warn('Unable to apply tags to stage logs');
				}

				params.request.metrics.counter(counterName, { status: 'success' }).incr();

				return unwrappedResult;
			} catch (error) {
				params.request.metrics.counter(counterName, { status: 'error' }).incr();

				throw error;
			} finally {
				stop();

				const latencyInMs = performance.now() - startTime;

				const isGateEnabled = params.request.statsig.checkGate(
					{ identifiers: { tenantId: params.request.orchestrationContext.tenantId ?? '' } },
					UNIFIED_FINISH_LOGS_GATE,
				);

				// When gate is ON: suppress message-level stages (high-volume, low-value) and only emit
				// placement-level stages when messages were filtered (delta != 0). Metrics are always
				// recorded regardless. When gate is OFF: always emit (old behavior).
				const defaultShouldLog = tag !== undefined ? hasDelta : () => false;
				const shouldEmit = isGateEnabled ? defaultShouldLog(tags) : true;

				if (shouldEmit) {
					// eslint-disable-next-line @atlassian/incredible/safe-logging-practices
					params.request.logger.info(
						{
							latencyInMs,
							// eslint-disable-next-line @post-office/platform-linting/no-object-spread-in-logger-tags
							...tags,
						},
						'Finished stage',
					);
				}
			}
		}) as T;

const createTimedSystemStage =
	<T extends AnyArityOneFunction>(fn: (context: { request: SyncRequestContext }) => T) =>
	(contexts: { request: RequestContext }): T => {
		return (async (params: ArityOneParameter<T>) => {
			const startTime = performance.now();

			const { stop } = contexts.request.metrics
				.histogram(
					timerName,
					HISTOGRAM_LATENCY_INTERVAL_BUCKETS,
					HIGH_CARDINALITY_METRIC_TAG_OVERRIDES,
				)
				.measure();

			const result = fn(contexts)(params);

			try {
				const unwrappedResult = (await result) as ReturnType<T>;

				contexts.request.metrics.counter(counterName, { status: 'success' }).incr();

				return unwrappedResult;
			} catch (error) {
				contexts.request.metrics.counter(counterName, { status: 'error' }).incr();

				throw error;
			} finally {
				stop();

				const latencyInMs = performance.now() - startTime;
				contexts.request.logger.info({ latencyInMs }, 'Finished stage');
			}
		}) as T;
	};

export const messageTimedStage: (
	stage: AnyBackendMessageStage | AnySyncBackendMessageStage,
) => AnyBackendMessageStage | AnySyncBackendMessageStage = createTimedStage<
	AnyBackendMessageStage | AnySyncBackendMessageStage
>({});

export const placementTimedStage: (stage: AnyBackendPlacementStage) => AnyBackendPlacementStage =
	createTimedStage<AnyBackendPlacementStage>({
		tag: ({ input, output }) => {
			if (!output) {
				return {};
			}

			const inputMessageTemplateIdsSet = new Set(
				input.data.messages.map((message) => message.messageTemplateId),
			);
			const inputMessageTemplateIds = Array.from(inputMessageTemplateIdsSet.values());

			const outputMessageTemplateIdsSet = new Set(
				output.messages.map((message) => message.messageTemplateId),
			);
			const outputMessageTemplateIds = Array.from(outputMessageTemplateIdsSet.values());

			const deltaMessageTemplateIds = inputMessageTemplateIds.filter(
				(id) => !outputMessageTemplateIdsSet.has(id),
			);

			const inputMessageInstanceIds = input.data.messages.map(
				({ messageInstanceId }) => messageInstanceId,
			);

			const outputMessageInstanceIdsSet = new Set(
				output.messages.map(({ messageInstanceId }) => messageInstanceId),
			);
			const outputMessageInstanceIds = Array.from(outputMessageInstanceIdsSet.values());

			const deltaMessageInstanceIds = inputMessageInstanceIds.filter(
				(id) => !outputMessageInstanceIdsSet.has(id),
			);

			return {
				input: {
					number: input.data.messages.length,
					messageTemplateIds: inputMessageTemplateIds,
					messageInstanceIds: inputMessageInstanceIds,
				},
				output: {
					number: output.messages.length,
					messageTemplateIds: outputMessageTemplateIds,
					messageInstanceIds: outputMessageInstanceIds,
				},
				delta: {
					number: output.messages.length - input.data.messages.length,
					messageTemplateIds: deltaMessageTemplateIds,
					messageInstanceIds: deltaMessageInstanceIds,
				},
			};
		},
	});

export const placementRuntimeTimedStage: <T extends AnyArityOneFunction>(
	fn: (context: { request: SyncRequestContext }) => T,
) => (contexts: { request: RequestContext }) => T = createTimedSystemStage;

export const logRuntimeResult =
	({ request }: { request: RequestContext }) =>
	({
		result,
		latencyInMs,
	}: {
		result: PlacementBackendRuntimeResult;
		latencyInMs?: number;
	}): void => {
		const errorMessageTemplateIds = Array.from(
			new Set(result.errors.map((e) => e.messageTemplateId)).values(),
		);

		const messageTemplateIds = Array.from(
			new Set(result.data.messages.map((e) => e.messageTemplateId)).values(),
		);

		const formattedMessages = result.data.messages.map(
			({
				messageTemplateId,
				messageInstanceId,
				messageCreationType,
				messageCategory,
				triggerId,
			}) => ({
				messageTemplateId,
				messageInstanceId,
				messageCreationType,
				messageCategory,
				triggerId,
			}),
		);

		const formattedErrorDetails = result.errors.map(
			({ messageTemplateId, messageInstanceId, reason }) => ({
				messageTemplateId,
				messageInstanceId,
				reason,
			}),
		);

		request.logger.info(
			{
				latencyInMs,
				errors: {
					number: result.errors.length,
					messageTemplateIds: errorMessageTemplateIds,
					messageDetails: formattedErrorDetails,
				},
				messages: {
					number: result.data.messages.length,
					messageTemplateIds: messageTemplateIds,
					messageDetails: formattedMessages,
				},
			},
			'Runtime result',
		);
	};
